import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Activity, BatteryLow, WifiOff, ShieldAlert, CheckCircle2, ChevronRight, X,
  Sunrise, Wrench, CalendarClock, Building2, Mail, Loader2, BellOff,
} from "lucide-react";
import api from "@/lib/api";

const URGENCY = {
  soon: { color: "#EF4444", label: "Plan soon" },
  scheduled: { color: "#F59E0B", label: "Scheduled" },
  ok: { color: "#34D399", label: "Plenty left" },
};

function fmtDate(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function Row({ icon: Icon, color, label, items, render, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">{label} ({items.length})</span>
      </div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-3 rounded-md bg-[#141B29] border border-[#232C40] px-3 py-1.5 text-sm">
            {render(it)}
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthModal({ data, onClose }) {
  const t = data.totals;
  const ok = data.overall === "healthy";
  const network = data.mode === "network";
  const m = data.maintenance || {};
  const [sending, setSending] = useState(false);
  const [snoozing, setSnoozing] = useState(null);
  const qc = useQueryClient();

  const snooze = async (it) => {
    if (!it.store_id || !it.device_id) { toast.info("Open Servicing to schedule this swap."); return; }
    setSnoozing(it.device_id);
    try {
      await api.post("/health/battery/snooze", { store_id: it.store_id, device_id: it.device_id });
      toast.success(`${it.chair} marked ordered — off the report until due`);
      qc.invalidateQueries({ queryKey: ["daily-health"] });
      qc.invalidateQueries({ queryKey: ["servicing-calendar"] });
    } catch { toast.error("Couldn't snooze — check your access to this store"); }
    setSnoozing(null);
  };

  const emailReport = async () => {
    setSending(true);
    try {
      const r = await api.post("/health/daily/email");
      if (r.data?.sent) {
        toast.success(`Morning report sent to ${r.data.recipient}`);
      } else {
        toast.info("In production this emails each owner automatically. No deliverable address is set for this demo account.");
      }
    } catch {
      toast.error("Couldn't send the report just now.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="health-modal" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-[#2A3550] bg-[#171E2E] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#1E2638] border border-[#2A3550]">
              <Sunrise className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h3 className="font-display text-lg font-medium tracking-tight text-white">Morning system check</h3>
              <p className="text-xs text-slate-400">Rebooted &amp; self-tested at 06:00 · closed at 00:00</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="health-modal-close" className="text-slate-400 hover:text-white transition-colors duration-200"><X className="h-5 w-5" /></button>
        </div>

        <div className={`mt-5 flex items-center gap-2 rounded-lg px-4 py-3 border ${ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-amber-500/30 bg-amber-500/10"}`} data-testid="health-modal-status">
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <ShieldAlert className="h-5 w-5 text-amber-400" />}
          <span className={`text-sm font-medium ${ok ? "text-emerald-300" : "text-amber-300"}`}>
            {ok
              ? "All chairs and sensors are healthy this morning."
              : network
                ? `${t.batteries_to_replace} batter${t.batteries_to_replace === 1 ? "y" : "ies"} to plan across ${t.stores_attention} of ${t.stores} store${t.stores === 1 ? "" : "s"} — nothing stops a chair working.`
                : `${t.issues} item${t.issues === 1 ? "" : "s"} to plan before the day starts — none stop a chair working.`}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-lg bg-[#141B29] border border-[#232C40] py-3">
            <div className="text-2xl font-display font-light text-white">{network ? t.stores : t.sensors}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{network ? "Stores" : "Sensors"}</div>
          </div>
          <div className="rounded-lg bg-[#141B29] border border-[#232C40] py-3">
            <div className="text-2xl font-display font-light text-emerald-400">{network ? t.stores_ok : t.healthy}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{network ? "All clear" : "Healthy"}</div>
          </div>
          <div className="rounded-lg bg-[#141B29] border border-[#232C40] py-3">
            <div className="text-2xl font-display font-light text-amber-400">{t.batteries_to_replace}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Batteries to plan</div>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {network && (
            <Row testid="health-stores-attention" icon={Building2} color="#38BDF8" label="Stores to look at" items={data.stores_attention}
              render={(it) => (
                <>
                  <span className="text-slate-200 truncate">#{it.store_number} · {it.store_name}</span>
                  <span className="text-xs text-slate-400 shrink-0">
                    {[it.low_battery ? `${it.low_battery} battery` : null, it.offline ? `${it.offline} offline` : null, it.tamper ? `${it.tamper} tamper` : null].filter(Boolean).join(" · ")}
                  </span>
                </>
              )} />
          )}

          <Row testid="health-low-battery" icon={BatteryLow} color="#F59E0B" label="Batteries to schedule a swap" items={data.low_battery}
            render={(it) => (
              <>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: (URGENCY[it.urgency] || URGENCY.scheduled).color }} />
                  <span className="min-w-0">
                    <span className="block text-slate-200 truncate">#{it.store_number} · {it.chair}</span>
                    <span className="flex items-center gap-1 text-[11px] text-slate-400">
                      <CalendarClock className="h-3 w-3 shrink-0" /> replace by {fmtDate(it.replace_by)}
                    </span>
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-amber-400 font-semibold">{it.battery}%</span>
                  {it.device_id && (
                    <button data-testid={`health-snooze-${it.device_id}`} disabled={snoozing === it.device_id} onClick={() => snooze(it)}
                      title="Mark ordered — off the report until due"
                      className="inline-flex items-center gap-1 rounded-md border border-[#2A3550] bg-[#1E2638] px-2 py-1 text-[11px] text-slate-300 hover:border-[#38BDF8] hover:text-white transition-colors duration-200 disabled:opacity-50">
                      {snoozing === it.device_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellOff className="h-3 w-3" />} Ordered
                    </button>
                  )}
                </span>
              </>
            )} />
          <Row testid="health-offline" icon={WifiOff} color="#EF4444" label="Offline sensors" items={data.offline_sensors}
            render={(it) => (<><span className="text-slate-200">#{it.store_number} · {it.chair}</span><span className="text-red-400 text-xs">No signal</span></>)} />
          <Row testid="health-tamper" icon={ShieldAlert} color="#EF4444" label="Possible tamper" items={data.tamper}
            render={(it) => (<><span className="text-slate-200">#{it.store_number} · {it.chair}</span><span className="text-red-400 text-xs">Check hardware</span></>)} />
          {ok && <p className="text-sm text-slate-400 text-center py-2">Nothing to action — every sensor reported in and battery levels are healthy.</p>}
        </div>

        <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-[#232C40] bg-[#141B29] px-4 py-3" data-testid="health-maintenance">
          <Wrench className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-400 leading-relaxed">
            <span className="text-slate-200 font-semibold">Easy to maintain.</span> Sensors run on a {m.battery_type || "long-life"} cell ({m.service_life || "multi-year life"}). {m.note}
          </p>
        </div>

        <button onClick={emailReport} disabled={sending} data-testid="health-email-report"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity duration-200 disabled:opacity-60"
          style={{ backgroundColor: "var(--brand)" }}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          {sending ? "Sending…" : "Email me this report"}
        </button>
        <p className="mt-2 text-[11px] text-slate-500 text-center">Owners &amp; managers get this digest on the maintenance schedule (weekly by default). Battery-failure and tamper alerts are always sent the same morning.</p>
      </motion.div>
    </div>
  );
}

export default function DailyHealthBar() {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["daily-health"],
    queryFn: () => api.get("/health/daily").then((r) => r.data),
  });
  if (!data) return null;
  const ok = data.overall === "healthy";
  const network = data.mode === "network";
  const t = data.totals;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="daily-health-bar"
        className="mt-5 w-full group flex items-center gap-3 rounded-lg border border-[#2A3550] bg-[#141B29]/60 px-4 py-2.5 text-left hover:border-[#38BDF8]/50 transition-colors duration-200"
      >
        <Activity className={`h-4 w-4 shrink-0 ${ok ? "text-emerald-400" : "text-amber-400"}`} />
        <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold shrink-0">Morning check · 06:00</span>
        <span className={`text-sm truncate ${ok ? "text-slate-300" : "text-amber-300"}`}>
          {ok
            ? "All chairs healthy"
            : network
              ? `${t.batteries_to_replace} batter${t.batteries_to_replace === 1 ? "y" : "ies"} to plan · ${t.stores_attention} store${t.stores_attention === 1 ? "" : "s"}`
              : `${t.issues} item${t.issues === 1 ? "" : "s"} to plan`}
        </span>
        <span className="ml-auto flex items-center gap-3 text-xs text-slate-500 shrink-0">
          <span className="hidden md:inline">{network ? `${t.stores} stores · ${t.stores_ok} all clear` : `${t.sensors} sensors · min battery ${data.battery.min}%`}</span>
          <ChevronRight className="h-4 w-4 group-hover:text-[#38BDF8] transition-colors duration-200" />
        </span>
      </button>
      {open && <HealthModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}
